import Event from '../../../utils/event';
import _ from '../../../utils/underscore';
import {SYSTEM_EVENTS} from '../../../events/events';
import {PENDING_ACTIONS} from '../../pending_action/pending_action_consts';
import {GNWP_SOURCES} from '../../../components/external_links/external_links_consts';
import {CASHIER_ACTIONS} from '../../../components/external_links/external_links_consts';
import BaseObject from '../../../base/base_object';
import DateTimeUtils from '../../../utils/date_time_utils';
import {DEEP_LINK_MAP} from '../../../components/external_links/handlers/external_links_base_handler_consts';
import {CHANNEL_ELEMENTS, CHANNEL_SOURCE} from "../../../../../common/const/casinofied-analytics-consts";
import {AreaType} from "@sparkware/uc-sdk-core/lib/apps/app-enums";
import ServiceFactory from '../../service_factory';

export default class GameCommonPostMessageActions extends BaseObject {
    static CLASS = 'GameCommonPostMessageActions';
    _serviceFactory: ServiceFactory;
    _gameOptions: any;
    _isReal: boolean;

    init(params) {
        super.init(params);

        if(!params.serviceFactory) {
            this.throwArgumentExpected('24', 'init', 'serviceFactory', params);
        }

        if(!params.gameOptions) {
            this.throwArgumentExpected('28', 'init', 'gameOptions', params);
        }

        if(_.isNullOrUndefined(params.isReal)) {
            this.throwArgumentExpected('32', 'init', 'isReal', params);
        }

        this._serviceFactory = params.serviceFactory;
        this._gameOptions = params.gameOptions;
        this._isReal = params.isReal;
    }

    lobby() {
        this.closegame({
            showNextGameSuggestion: true
        });
    }

    myaccountresponsiblegaming() {
        this.closegame();
        this._serviceFactory.openLinkService().openLink({
            source: GNWP_SOURCES.MY_ACCOUNT,
            options: {tabId: DEEP_LINK_MAP.MY_ACCOUNT_DL_RESPONSIBLE_GAMING}
        });
    }

    keepalive() {
        Event.fire(SYSTEM_EVENTS.EXTEND_USER_TIME_TO_LIVE);
    }

    _openCashier() {
        this._serviceFactory.cashierProxyService().openCashier({
            sessionType: CASHIER_ACTIONS.FULL_CASHIER,
            channel: {
                source: CHANNEL_SOURCE.casinoClient,
                area: AreaType.InGame,
                element: CHANNEL_ELEMENTS.depositButton
            }
        });
    }

    cashier() {
        const pendingAction: any = {
            name: PENDING_ACTIONS.GAME,
            persistent: true,
            options: {
                gameType: this._gameOptions.gameType,
                gameName: this._gameOptions.gameName,
                isReal: this._isReal,
                forceLogin: this._isReal
            }
        };

        // for 888 vouchers, when user is coming back from cashier page (external site)
        // in FreePlayConfirmationGameStep we miss the voucher because
        // free play firebase data comes AFTER running this step 
        // so to solve this scenario we store in game data freePlayData information
        // (if is the case)
        if (this._gameOptions.freePlayData && !this._gameOptions.freePlayData.isThirdParty) {
            pendingAction.options.freePlayData = this._gameOptions.freePlayData;
        }

        this._serviceFactory.pendingActionService().addPendingAction(pendingAction);
        this._openCashier();
    }

    cashierAndCloseGame() {
        if (this._isReal && this._gameOptions.company.supportsRefreshBalance()) {
            // for this special case we only open cashier and let the game update balance after we deposit widget is closed 
            // we don't need to close the game anymore
            this._openCashier();
        } else {
            this.closegame();
            this._openCashier();
        }
    }

    reportanalytics(analyticsData) {
        let defaultAnalyticsData = {
            category: this._isReal ? 'Client_GameFrame_Real' : 'Client_GameFrame_Anonymous',
            label: this._gameOptions.gameType + '_' + this._gameOptions.gameName.replace(/ /g, '')
        };

        Event.fire(SYSTEM_EVENTS.ANALYTIC_TRIGGER_ACTIVATED, Object.assign(defaultAnalyticsData, analyticsData));
    }

    switchtoreal() {
        this.closegame();
        this._serviceFactory.gameService().openRealGame({...this._extractReloadGameOptions(), skipFreePlay:true});
    }

    _extractReloadGameOptions() {
        return {
            gameType: this._gameOptions.gameType,
            gameName: this._gameOptions.gameName,
            marketing: this._gameOptions.marketing,
            referrerId: this._gameOptions.referrerId
        };
    }

    sessionid(id) {
        if(this._isReal) {
            if(this._gameOptions.gameNavigationBar) {
                this._updateSessionIdInNavigationBar(id);
            }
        }
    }

    _updateSessionIdInNavigationBar(sessionId) {
        this._gameOptions.gameNavigationBar.setGameSessionId(sessionId);
    }

    changeregulationdata(regulationData) {
        if(this._gameOptions.gameRegulationStripe?.setRegulationData) {
            this._gameOptions.gameRegulationStripe.setRegulationData(regulationData);
        }

		if (this._gameOptions.gameSessionNotificationManager) {
			this._gameOptions.gameSessionNotificationManager.updateRegulationData(regulationData);
		}

        if (this._gameOptions.gameSessionSummaryHandler) {
            this._gameOptions.gameSessionSummaryHandler.updateSummaryData(regulationData);
        }
    }

    responsiblegaming() {
        this._serviceFactory.openLinkService().openResponsibleGamblingLink(this._gameOptions.responsibleGamblingView);
    }

    history() {
        this.closegame();
        Event.fire(SYSTEM_EVENTS.ANALYTIC_TRIGGER_ACTIVATED, {category: 'Client_RealityCheck_Reminder', action: 'ActionDropDown', label: 'History'});
        this._serviceFactory.integrationService().redirectToHistory();
    }

    closegame(options = {}) {
        if (this._gameOptions.gameWindow) {
            this._gameOptions.gameWindow.closeGameWindow(options);
        } else {
            // We need it because standalone games(for example NYX in spain) don't have game window.
            Event.fire(SYSTEM_EVENTS.CLOSE_GAME, {
                gameType: this._gameOptions.gameType,
                isReal: this._isReal
            });
        }
    }

    reloadgame() {
        this.closegame();

        if (this._isReal) {
            this._serviceFactory.gameService().openRealGame(this._extractReloadGameOptions());
        } else {
            this._serviceFactory.gameService().openAnonymousGame(this._extractReloadGameOptions());
        }

    }

    onprogress(progress) {
        // we don't do anything yet on this action
    }

    gameready() {
        this._gameOptions.endLoadingTime = new Date();
        this._reportGameLoadingTime();

        if(this._gameOptions.fullScreenTapComponent){
            this._gameOptions.gameWindow.appendChildComponent(this._gameOptions.fullScreenTapComponent);
        }
    }

    _reportGameLoadingTime() {
        if(!this._gameOptions.startLoadingTime) {
            return;
        }

        let gameLoadingTime = DateTimeUtils.millisecondsToSeconds(this._gameOptions.endLoadingTime - this._gameOptions.startLoadingTime);

        let gameMode = this._isReal ? 'Real' : 'Anonymous';

        this.reportanalytics({
            category: '[Technical-event]',
            action: `Game_LoadingTime_In_Seconds_${gameMode}`,
            label: this._backGameData(this._gameOptions),
            value: gameLoadingTime
        });
    }

    _backGameData(gameOptions){
        const gameURLBuilderType = (gameOptions.gameUrlBuilder&&gameOptions.gameUrlBuilder.getClassName)?'_'+gameOptions.gameUrlBuilder.getClassName():'';
        let response = `${gameOptions.companyName}_${gameOptions.vendorName}_${gameOptions.gameType}_${gameOptions.gameName}${gameURLBuilderType}`;

        return response.replace(/ /g, '');
    }

    sound(soundOn) {
        if(soundOn) {
            this._sendAnalytics('SoundOn');
        } else {
            this._sendAnalytics('SoundOff');
        }
    }

    disablerealitycheck() {
        this._serviceFactory.userService().updateRealityCheckData(0);
    }

    _sendAnalytics(action) {
        return {
            category: this._isReal ? 'Client_GameFrame_Real' : 'Client_GameFrame_Anonymous',
            action: action,
            label: this._gameOptions.gameType + '_' + this._gameOptions.gameName.replace(/ /g, '')
        }
    }

    gameroundstart() {
        this.keepalive();
    }

	gameroundend() {
        this.keepalive();
    }

    finishround() { }

    gamelauncherready(params){
        this._sendGameLauncherElasticData('Game_Launcher_Ready', params.gameUrl);
    }

    gamelaunchergameerror(params){
        this._sendGameLauncherElasticData('Game_Launcher_Error', params.message, true);
    }

    _sendGameLauncherElasticData(action: string, label: string, isError?: boolean) {
        const eventData = {
            CGPEventCategory: '888_Game_Launcher',
            CGPEventAction: action,
            CGPEventLabel:label,
        };

        if(isError){
            this._serviceFactory.structuredLoggingService().getLogger(eventData).error();
        }else {
            this._serviceFactory.structuredLoggingService().getLogger(eventData).info();
        }
    }

    livegameswitched() {
        Event.fire(SYSTEM_EVENTS.LIVE_GAME_SWITCHED);
    }
}
